import React, { useEffect, useState } from "react";
import { settingsAPI } from "../services/api";
import { LANGUAGES, useI18n } from "../i18n";
import LanguageSelect from "../components/LanguageSelect";
import { useMembership } from "../context/MembershipContext";

/**
 * The shop's operational settings, as a form.
 *
 * It used to list whatever settings rows the shop happened to have, each as a
 * text box — so a shop with none saw an empty card, and a language had to be
 * typed. The settings the code actually reads are listed here with their
 * defaults, and saving writes only the ones that changed.
 */
const DEFAULTS = {
  default_language: "en",
  low_stock_threshold: "10",
  low_stock_push_enabled: "true",
};

const Settings = () => {
  const { t } = useI18n();
  const { can } = useMembership();
  const [values, setValues] = useState(DEFAULTS);
  const [saved, setSaved] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let cancelled = false;

    settingsAPI
      .getAll()
      .then(({ data }) => {
        if (cancelled) return;

        const stored = Object.fromEntries(
          Object.entries(data || {})
            .filter(([key]) => key in DEFAULTS)
            .map(([key, value]) => [key, String(value)]),
        );
        const merged = { ...DEFAULTS, ...stored };

        setValues(merged);
        setSaved(merged);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.error || t("settings.loadFailed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changed = Object.keys(DEFAULTS).filter(
    (key) => values[key] !== saved[key],
  );

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");

    try {
      await Promise.all(
        changed.map((key) => settingsAPI.set(key, values[key])),
      );
      setSaved(values);
      setNotice(t("settings.saved"));
    } catch (err) {
      setError(
        err.response?.status === 403
          ? t("settings.ownerOnly")
          : err.response?.data?.message ||
              err.response?.data?.error ||
              t("settings.saveFailed"),
      );
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600"></div>
      </div>
    );
  }

  const field =
    "mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500";

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">
          {t("settings.title")}
        </h1>
        <p className="text-gray-600">{t("settings.subtitle")}</p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {notice && !error && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      <form
        onSubmit={save}
        className="bg-white rounded-xl shadow-lg p-4 sm:p-6 space-y-5"
      >
        {/* Readable to anyone given Settings; changed only with edit */}
        <fieldset
          disabled={!can("settings.edit")}
          className="min-w-0 border-0 p-0 m-0 space-y-4"
        >
          <h2 className="text-lg font-semibold text-gray-800">
            {t("settings.shopSection")}
          </h2>

          <div className="grid gap-5 md:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("settings.defaultLanguage")}
              </span>
              <select
                value={values.default_language}
                onChange={(e) =>
                  setValues({ ...values, default_language: e.target.value })
                }
                className={field}
              >
                {LANGUAGES.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.label}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-gray-500">
                {t("settings.defaultLanguageHint")}
              </span>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("settings.lowStock")}
              </span>
              <input
                type="number"
                min="0"
                step="1"
                required
                dir="ltr"
                value={values.low_stock_threshold}
                onChange={(e) =>
                  setValues({ ...values, low_stock_threshold: e.target.value })
                }
                className={field}
              />
              <span className="mt-1 block text-xs text-gray-500">
                {t("settings.lowStockHint")}
              </span>
            </label>

            <label className="flex items-center gap-2 md:col-span-2">
              <input
                type="checkbox"
                checked={values.low_stock_push_enabled === "true"}
                onChange={(e) =>
                  setValues({
                    ...values,
                    low_stock_push_enabled: e.target.checked ? "true" : "false",
                  })
                }
                className="h-4 w-4"
              />
              <span className="text-sm text-gray-700">
                {t("settings.lowStockPush")}
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={busy || changed.length === 0}
            className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-50"
          >
            {busy ? t("common.saving") : t("common.save")}
          </button>
        </fieldset>
      </form>

      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-gray-800">
          {t("settings.deviceSection")}
        </h2>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-700">
            {t("settings.deviceLanguage")}
          </span>
          <LanguageSelect />
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {t("settings.deviceLanguageHint")}
        </p>
      </div>
    </div>
  );
};

export default Settings;
